/**
 * /auth/desktop on the website: where the desktop app sends someone to sign in (Google included),
 * and the way back to the app with a one-time code. See lib/desktopSignIn.ts.
 *
 * Signed out, it remembers where to come back to and shows the sign-in page; signed in, it makes the
 * code and opens devpulse://signed-in, with a button in case the browser asks first.
 */

import { useEffect, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { getSession } from '../lib/auth'
import { backToApp } from '../lib/desktopSignIn'
import { useI18n } from '../lib/i18n'
import { rememberAfterSignIn } from '../lib/afterSignIn'

export default function DesktopSignIn() {
  const { t } = useI18n()
  const [params] = useSearchParams()
  const state = params.get('state') ?? ''
  const valid = /^[A-Za-z0-9_-]{16,100}$/.test(state)
  const signedIn = Boolean(getSession())
  const [link, setLink] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!signedIn || !valid) return
    let alive = true
    api.auth
      .handoff()
      .then(({ code }) => {
        if (!alive) return
        const back = backToApp(code, state)
        setLink(back)
        window.location.href = back
      })
      .catch(() => alive && setFailed(true))
    return () => {
      alive = false
    }
  }, [signedIn, valid, state])

  if (!valid) return <Navigate to="/start" replace />
  if (!signedIn) {
    rememberAfterSignIn(`/auth/desktop?state=${state}`)
    return <Navigate to="/sign-in" replace />
  }

  return (
    <div className="auth">
      <div className="auth__card stack" style={{ alignItems: 'center', textAlign: 'center' }}>
        <strong>{t('desktopSignIn.title')}</strong>
        {failed ? (
          <p className="small" role="alert">{t('desktopSignIn.failed')}</p>
        ) : (
          <>
            <p className="small muted" style={{ margin: 0 }}>{t('desktopSignIn.body')}</p>
            {link && (
              <a className="btn btn--primary" href={link}>
                {t('desktopSignIn.open')}
              </a>
            )}
          </>
        )}
      </div>
    </div>
  )
}
